<script lang="ts">
    import { createEventDispatcher, onMount, tick } from "svelte";
    import { fade, scale } from "svelte/transition";
    import CardBack from "./CardBack.svelte";
    import Card from "./Card.svelte";
    import { CARD_BACKS, CARD_FACES, BOARD_COLORS, GAME_MODES } from "./lib/content";
    import type { SoundHooks } from "./lib/soundHooks";
    import { ensureAudioContext } from "./lib/soundManager";

    export let cardFace: string;
    export let cardBack: string | number;
    export let boardColor: string;
    export let soundEnabled: boolean;
    export let leftHanded: boolean;
    export let gameMode: string = "klondike";
    export let soundHooks: SoundHooks | null = null;
    export let unlockedItems: Set<string> = new Set();
    export let pendingUnlock: string | null = null;

    const dispatch = createEventDispatcher();

    let currentView: "main" | "faces" | "backs" | "boards" = "main";
    let hoveredBackId: string | null = null;
    let isScrolling = false;

    $: activeBackId =
        hoveredBackId ||
        (typeof cardBack === "string" ? cardBack : CARD_BACKS[0].id);
    $: activeBackDef =
        CARD_BACKS.find((b) => b.id === activeBackId) || CARD_BACKS[0];

    $: sortedBacks = [...CARD_BACKS].sort((a, b) => {
        const aUnlocked = unlockedItems.has(a.id);
        const bUnlocked = unlockedItems.has(b.id);
        if (aUnlocked && !bUnlocked) return -1;
        if (!aUnlocked && bUnlocked) return 1;
        return 0;
    });

    $: unlockedBoardCount = BOARD_COLORS.filter(
        (b) => !b.locked || unlockedItems.has(b.id),
    ).length;

    let showCopyToast = false;

    function copyDealUrl(seed: string) {
        const url = new URL(window.location.href);
        url.searchParams.set("deal", seed);
        url.searchParams.set("mode", gameMode);
        navigator.clipboard.writeText(url.toString()).then(() => {
            showCopyToast = true;
            setTimeout(() => {
                showCopyToast = false;
            }, 2000);
        });
    }

    async function showBacks() {
        isScrolling = true;
        currentView = "backs";
        await tick();
        const el = document.querySelector(".grid-item.selected");
        if (el) {
            el.scrollIntoView({ behavior: "auto", block: "center" });
        }
        requestAnimationFrame(() => {
            isScrolling = false;
        });
    }

    onMount(async () => {
        if (pendingUnlock) {
            isScrolling = true;
            if (CARD_FACES.some((f) => f.id === pendingUnlock))
                currentView = "faces";
            if (CARD_BACKS.some((b) => b.id === pendingUnlock))
                currentView = "backs";
            if (BOARD_COLORS.some((b) => b.id === pendingUnlock))
                currentView = "boards";

            // Scroll to the new unlock after a brief delay to allow rendering
            await tick();
            if (pendingUnlock) {
                const el = document.querySelector(
                    `[data-id="${pendingUnlock}"]`,
                );
                if (el) {
                    el.scrollIntoView({
                        behavior: "auto",
                        block: "center",
                    });
                }
            }
            requestAnimationFrame(() => {
                isScrolling = false;
            });
        }
    });

    function close() {
        dispatch("close");
    }

    function selectFace(face: string) {
        if (!unlockedItems.has(face)) return;
        cardFace = face;
        dispatch("change", { cardFace });
    }

    function selectBack(back: string) {
        if (!unlockedItems.has(back)) {
            // Shake animation trigger could go here if we had a way to signal it to the specific element
            // For now, the CSS :active state on .locked will handle some feedback,
            // but we can add a temporary class if needed.
            return;
        }
        cardBack = back;
        dispatch("change", { cardBack });
    }

    function selectBoard(board: string) {
        if (
            !unlockedItems.has(board) &&
            BOARD_COLORS.find((b) => b.id === board)?.locked
        )
            return;
        boardColor = board;
        dispatch("change", { boardColor });
    }

    function handleBackHover(id: string) {
        hoveredBackId = id;
    }

    function handleBackLeave() {
        hoveredBackId = null;
    }

    function toggleSound() {
        soundEnabled = !soundEnabled;
        if (soundEnabled && soundHooks) {
            // Ensure audio context is initialized and ready
            ensureAudioContext();
            // Use setTimeout to ensure audio context is ready
            setTimeout(() => {
                soundHooks?.soundEnabled();
            }, 10);
        }
        dispatch("change", { soundEnabled });
    }

    function toggleHandedness() {
        leftHanded = !leftHanded;
        dispatch("change", { leftHanded });
    }
</script>

<div
    class="overlay"
    on:click|self={close}
    on:keydown={(e) => e.key === "Escape" && close()}
    role="button"
    tabindex="0"
>
    <div class="menu" in:scale={{ duration: 400, start: 0.95 }}>
        <div class="header">
            {#if currentView !== "main"}
                <button
                    class="back-btn"
                    on:click={() => (currentView = "main")}
                >
                    ←
                </button>
            {/if}
            <h2>Settings</h2>
            <button class="close-btn" on:click={close}>×</button>
        </div>

        <div class="content">
            {#if currentView === "main"}
                <button
                    class="menu-item"
                    on:click={() => (currentView = "faces")}
                >
                    <span>Card Faces</span>
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <div
                            class="mini-back"
                            style="width: 40px; height: 56px;"
                        >
                            <div
                                class="scaled-back"
                                style="width: 100px; height: 140px; transform: scale(0.4); transform-origin: top left;"
                            >
                                {#if cardFace === "face_standard"}
                                    <div
                                        class="preview-card high-contrast"
                                        style="width: 100px; height: 140px;"
                                    >
                                        <div class="hc-top">
                                            <span class="hc-rank">A</span>
                                            <span class="hc-suit-small">♠</span
                                            >
                                        </div>
                                        <div class="hc-bottom">
                                            <span class="hc-suit-large">♠</span
                                            >
                                        </div>
                                    </div>
                                {:else}
                                    <div
                                        class="preview-card"
                                        style="width: 100px; height: 140px;"
                                    >
                                        <img
                                            src="{import.meta.env
                                                .BASE_URL}card_faces3/AS.svg"
                                            alt="Classic"
                                        />
                                    </div>
                                {/if}
                            </div>
                        </div>
                        <span class="chevron">›</span>
                    </div>
                </button>

                <button class="menu-item" on:click={showBacks}>
                    <span>Card Backs</span>
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <div
                            class="mini-back"
                            style="width: 40px; height: 56px;"
                        >
                            <div
                                class="scaled-back"
                                style="width: 100px; height: 140px; transform: scale(0.4); transform-origin: top left;"
                            >
                                <CardBack variant={cardBack} />
                            </div>
                        </div>
                        <span class="chevron">›</span>
                    </div>
                </button>

                {#if unlockedBoardCount > 1}
                    <button
                        class="menu-item"
                        on:click={() => (currentView = "boards")}
                    >
                        <span>Board Color</span>
                        <div
                            style="display: flex; align-items: center; gap: 12px;"
                        >
                            <div
                                class="mini-back"
                                style="width: 40px; height: 40px; border-radius: 50%; background-color: {BOARD_COLORS.find(
                                    (b) => b.id === boardColor,
                                )?.value ||
                                    '#2e8b57'}; border: 2px solid rgba(255,255,255,0.2);"
                            ></div>
                            <span class="chevron">›</span>
                        </div>
                    </button>
                {/if}

                <button class="menu-item" on:click={toggleSound}>
                    <span>Sound Effects</span>
                    <div
                        class="toggle-switch"
                        class:checked={soundEnabled}
                    ></div>
                </button>

                <button class="menu-item" on:click={toggleHandedness}>
                    <span>Left Handed Mode</span>
                    <div class="toggle-switch" class:checked={leftHanded}></div>
                </button>
            {:else if currentView === "faces"}
                <div class="grid faces-grid">
                    {#each CARD_FACES as face}
                        <button
                            class="grid-item"
                            data-id={face.id}
                            class:selected={cardFace === face.id}
                            class:locked={!unlockedItems.has(face.id)}
                            on:click={() => selectFace(face.id)}
                            disabled={!unlockedItems.has(face.id)}
                        >
                            {#if pendingUnlock === face.id}
                                <!-- Removed new-badge -->
                            {/if}
                            {#if !unlockedItems.has(face.id)}
                                <div
                                    class="preview-card locked-placeholder"
                                    style="width: 100px; height: 140px;"
                                >
                                    <span class="lock-icon">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            ><rect
                                                x="3"
                                                y="11"
                                                width="18"
                                                height="11"
                                                rx="2"
                                                ry="2"
                                            ></rect><path
                                                d="M7 11V7a5 5 0 0 1 10 0v4"
                                            ></path></svg
                                        >
                                    </span>
                                </div>
                            {:else if face.id === "face_standard"}
                                <div
                                    class="preview-card high-contrast"
                                    style="width: 100px; height: 140px;"
                                >
                                    <div class="hc-top">
                                        <span class="hc-rank">A</span>
                                        <span class="hc-suit-small">♠</span>
                                    </div>
                                    <div class="hc-bottom">
                                        <span class="hc-suit-large">♠</span>
                                    </div>
                                </div>
                            {:else if face.id === "face_classic"}
                                <div
                                    class="preview-card"
                                    style="width: 100px; height: 140px;"
                                >
                                    <img
                                        src="{import.meta.env
                                            .BASE_URL}card_faces3/AS.svg"
                                        alt="Classic"
                                    />
                                </div>
                            {:else}
                                <div
                                    class="preview-card"
                                    style="width: 100px; height: 140px;"
                                >
                                    <img src={face.value} alt={face.name} />
                                </div>
                            {/if}
                            <span>{face.name}</span>
                        </button>
                    {/each}
                </div>
            {:else if currentView === "backs"}
                <div
                    class="grid backs-grid"
                    style:visibility={isScrolling ? "hidden" : "visible"}
                >
                    {#each sortedBacks as back}
                        <button
                            class="grid-item back-item"
                            data-id={back.id}
                            class:selected={cardBack === back.id}
                            class:locked={!unlockedItems.has(back.id)}
                            on:click={() => selectBack(back.id)}
                            on:mouseenter={() => handleBackHover(back.id)}
                            on:mouseleave={handleBackLeave}
                            disabled={!unlockedItems.has(back.id)}
                        >
                            <div class="mini-back">
                                <div class="scaled-back">
                                    {#if pendingUnlock === back.id}
                                        <!-- Removed new-badge -->
                                    {/if}
                                    {#if !unlockedItems.has(back.id)}
                                        <div class="locked-placeholder">
                                            <span class="lock-icon">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    ><rect
                                                        x="3"
                                                        y="11"
                                                        width="18"
                                                        height="11"
                                                        rx="2"
                                                        ry="2"
                                                    ></rect><path
                                                        d="M7 11V7a5 5 0 0 1 10 0v4"
                                                    ></path></svg
                                                >
                                            </span>
                                        </div>
                                    {:else}
                                        <CardBack variant={back.id} />
                                    {/if}
                                </div>
                            </div>
                        </button>
                    {/each}
                </div>
            {:else if currentView === "boards"}
                <div class="grid boards-grid">
                    {#each BOARD_COLORS as board}
                        {#if !board.locked || unlockedItems.has(board.id)}
                            <button
                                class="grid-item board-item"
                                data-id={board.id}
                                class:selected={boardColor === board.id}
                                on:click={() => selectBoard(board.id)}
                            >
                                {#if pendingUnlock === board.id}
                                    <!-- Removed new-badge -->
                                {/if}
                                <div
                                    class="preview-board"
                                    style="width: 80px; height: 80px; border-radius: 50%; background-color: {board.value}; border: 4px solid {boardColor ===
                                    board.id
                                        ? 'white'
                                        : 'rgba(255,255,255,0.1)'}; box-shadow: 0 4px 10px rgba(0,0,0,0.3);"
                                ></div>
                                <span>{board.name}</span>
                            </button>
                        {/if}
                    {/each}
                </div>
            {/if}
        </div>
        {#if currentView === "backs" && unlockedItems.has(activeBackDef.id)}
            <div class="details-footer">
                <div class="details-content">
                    <div class="details-title">{activeBackDef.name}</div>
                    {#if activeBackDef.artist || activeBackDef.year}
                        <div class="details-meta">
                            {activeBackDef.artist ||
                                "Unknown Artist"}{#if activeBackDef.year}, {activeBackDef.year}{/if}
                        </div>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
    {#if showCopyToast}
        <div class="toast" transition:fade>Card back deal URL copied</div>
    {/if}
</div>

<style>
    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        /* Background handled by App.svelte shared-backdrop */
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
    }

    .menu {
        background: #2c3e50;
        color: white;
        width: 90%;
        max-width: 400px;
        border-radius: 16px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        max-height: 80vh;
        position: relative; /* Ensure absolute children are relative to menu */
    }

    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        background: rgba(0, 0, 0, 0.2);
    }

    h2 {
        margin: 0;
        font-size: 1.2rem;
        flex: 1;
        text-align: center;
    }

    .back-btn,
    .close-btn {
        background: none;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .back-btn:hover,
    .close-btn:hover {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
    }

    .content {
        padding: 16px;
        overflow-y: auto;
        overflow-x: hidden;
        position: relative;
        flex: 1;
    }

    .menu-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 16px;
        background: rgba(255, 255, 255, 0.05);
        border: none;
        border-radius: 8px;
        color: white;
        margin-bottom: 8px;
        cursor: pointer;
        transition: background 0.2s;
        font-size: 1rem;
    }

    .menu-item:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    .mini-back {
        width: 100%;
        height: 100%;
        aspect-ratio: 5/7;
        position: relative;
        transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .scaled-back {
        width: 100%;
        height: 100%;
        transform: none;
        transform-origin: center;
    }

    .grid {
        display: grid;
        gap: 4px;
    }

    .faces-grid {
        grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
        gap: 12px;
    }

    .backs-grid {
        grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    }

    .boards-grid {
        grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    }

    .grid-item {
        background: none;
        border: 2px solid transparent;
        border-radius: 8px;
        padding: 0;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: white;
        position: relative;
        transition: transform 0.1s;
        /* overflow: hidden; Removed to allow hover scaling */
        z-index: 1;
    }

    .grid-item:not(.locked):hover {
        background: rgba(255, 255, 255, 0.05);
        z-index: 10;
    }

    /* Desktop Hover Effect */
    @media (hover: hover) {
        .back-item:not(.locked):hover .mini-back {
            transform: scale(1.05) translateY(-5px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
        }
    }

    /* Mobile Tap Effect & Desktop Click Override */
    .back-item:not(.locked):active .mini-back {
        transform: scale(0.9) translateY(0);
        transition-duration: 0.05s;
    }

    .grid-item.selected {
        border-color: #3498db;
        background: rgba(52, 152, 219, 0.1);
    }

    .details-footer {
        position: relative;
        width: 100%;
        background: rgba(44, 62, 80, 0.6);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        padding: 16px;
        box-sizing: border-box;
        transform: translateY(0);
        transition: transform 0.3s ease-out;
        z-index: 20;
    }

    .details-content {
        text-align: center;
        user-select: text;
        -webkit-user-select: text;
    }

    .details-title {
        font-weight: bold;
        font-size: 1.1rem;
        margin-bottom: 4px;
        color: white;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .details-meta {
        font-size: 0.95rem;
        color: rgba(255, 255, 255, 0.7);
        font-style: italic;
    }

    .preview-card {
        width: 100px;
        height: 140px;
        border-radius: 8px;
        overflow: hidden;
        background: white;
        position: relative;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    .preview-card img {
        width: 100%;
        height: 100%;
    }

    .high-contrast {
        display: flex;
        flex-direction: column;
        color: black;
        padding: 4px;
        box-sizing: border-box;
    }

    .hc-top {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
    }

    .hc-rank {
        font-size: 42px;
        font-weight: 900;
    }

    .hc-suit-small {
        font-size: 32px;
    }

    .hc-bottom {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .hc-suit-large {
        font-size: 64px;
        line-height: 1;
    }

    .toggle-switch {
        width: 50px;
        height: 30px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 15px;
        position: relative;
        transition: background 0.3s;
    }

    .toggle-switch.checked {
        background: #4cd964;
    }

    .toggle-switch::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        width: 26px;
        height: 26px;
        background: white;
        border-radius: 50%;
        transition: transform 0.3s;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    .toggle-switch.checked::after {
        transform: translateX(20px);
    }

    .toast {
        position: absolute;
        bottom: 80px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 0.9rem;
        pointer-events: none;
        z-index: 10000;
        white-space: nowrap;
    }

    .chevron {
        font-size: 1.2rem;
        opacity: 0.5;
        font-weight: bold;
    }

    .grid-item.locked {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .locked-placeholder {
        width: 100%;
        height: 100%;
        background: #333;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
    }

    .lock-icon {
        color: rgba(255, 255, 255, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
    }

</style>
